import { useCallback, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import FormProvider from 'src/components/hook-form';
import { axiosPostCall } from 'src/services/apisCall';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import UploadArea from 'src/components/upload/upload-area';

const DownloadLink = styled(Typography)(({ theme }) => ({
  cursor: 'pointer',
  fontWeight: 600,
  color: theme.palette.primary.main,
  textDecoration: 'underline',
  textDecorationColor: theme.palette.primary.main,
  '&:hover': {
    color: theme.palette.primary.dark,
    textDecorationColor: theme.palette.primary.dark,
  },
  transition: 'color 0.2s ease',
}));

const SubmitButton = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 24px',
  borderRadius: '8px',
  backgroundColor: theme.palette.primary.main,
  cursor: 'pointer',
  minWidth: '160px',
  height: '42px',
  transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    boxShadow: '0 4px 12px rgba(12, 102, 228, 0.35)',
  },
  '&:active': {
    backgroundColor: theme.palette.primary.darker,
  },
}));

const BulkUserImportForm = ({ setBulkModalOpen }) => {
  const [bulkTemplateFile, setBulkTemplateFile] = useState(null);
  const [uploadErrors, setUploadErrors] = useState(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const methods = useForm({
    defaultValues: {
      // file: null,
    },
  });

  const { handleSubmit, reset } = methods;

  const handleDownloadErrors = () => {
    if (!uploadErrors || uploadErrors.length === 0) return;

    const headers = ['Row no', 'emailAddress', 'userName', 'fullName', 'errors'];
    const csvContent = [
      headers.join(','),
      ...uploadErrors.map(err => `"${err.row}","${err.emailAddress}","${err.userName}","${err.fullName}","${err.errors}"`)
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'bulk_user_upload_errors.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = () => {
    const sampleData = [
      {
        "parentUserId": 1024589,
        "emailAddress": "rohan.sharma@techverve.com",
        "userName": "rohansharma",
        "contactNumber": 9876543210,
        "fullName": "Rohan Sharma",
        "password": "P@ssword2026!",
        "companyName": "TechVerve Solutions",
        "companyLocation": "Mumbai",
        "city": "Mumbai",
        "address": "401, Bandra Kurla Complex",
        "zipcode": "400051",
        "countryCode": "91",
        "aliasEmailAddress": "rohan.s@techverve.com",
        "products": "Console,Broadcast",
        "productPermissions": "[{\"productName\":\"Console\",\"permission\":\"l1\"},{\"productName\":\"Broadcast\",\"permission\":\"l1\"}]"
      },
      {
        "parentUserId": 4556123,
        "emailAddress": "priya.nair@qubit.io",
        "userName": "priyanair",
        "contactNumber": 9123456789,
        "fullName": "Priya Nair",
        "password": "Qub1t#Secure9",
        "companyName": "Qubit Labs",
        "companyLocation": "Bengaluru",
        "city": "Bengaluru",
        "address": "Indiranagar, 100 Feet Road",
        "zipcode": "560038",
        "products": "SMS,Broadcast",
        "productPermissions": "[{\"productName\":\"SMS\",\"permission\":\"l4\"},{\"productName\":\"Broadcast\",\"permission\":\"l1\"}]"
      },
      {
        "parentUserId": 8892143,
        "emailAddress": "amit.patel@apexcorp.in",
        "userName": "amitpatel",
        "contactNumber": 9560123456,
        "fullName": "Amit Patel",
        "password": "Am1t$Patel#99",
        "companyName": "Apex Corporation",
        "companyLocation": "Ahmedabad",
        "city": "Ahmedabad",
        "address": "SG Highway, Titanium Square",
        "zipcode": "380054",
        "countryCode": "91",
        "aliasEmailAddress": "apatel@apexcorp.in",
        "products": "Console,SMS,WaApi",
        "productPermissions": "[{\"productName\":\"Console\",\"permission\":\"l1\"},{\"productName\":\"SMS\",\"permission\":\"l4\"},{\"productName\":\"WaApi\",\"permission\":\"l1\"}]"
      },
      {
        "parentUserId": 2341987,
        "emailAddress": "sneha.gupta@cloudnest.com",
        "userName": "snehagupta",
        "contactNumber": 9811223344,
        "fullName": "Sneha Gupta",
        "password": "Cl0ud#Nest%20",
        "companyName": "CloudNest Technologies",
        "companyLocation": "Gurugram",
        "city": "Gurugram",
        "address": "Cyber City, Phase 3",
        "zipcode": "122002",
        "countryCode": "91",
        "aliasEmailAddress": "sneha@cloudnest.com",
        "products": "Console",
        "productPermissions": "[{\"productName\":\"Console\",\"permission\":\"l4\"}]"
      },
      {
        "parentUserId": 7654321,
        "emailAddress": "vikram.das@orion.com",
        "userName": "vikramdas",
        "contactNumber": 9444012345,
        "fullName": "Vikram Das",
        "password": "V1kram@Or1on!",
        "companyName": "Orion Digital",
        "companyLocation": "Kolkata",
        "city": "Kolkata",
        "address": "Salt Lake, Sector V",
        "zipcode": "700091",
        "countryCode": "91",
        "aliasEmailAddress": "vdas@orion.com",
        "products": "SMS",
        "productPermissions": "[{\"productName\":\"SMS\",\"permission\":\"l1\"}]"
      }
    ];

    const headers = ['parentUserId', 'emailAddress', 'userName', 'contactNumber', 'fullName', 'password', 'companyName', 'companyLocation', 'city', 'address', 'zipcode', 'countryCode', 'products', 'productPermissions', "aliasEmailAddress"];

    const csvContent = [
      headers.join(','),
      ...sampleData.map(row => {
        // 1. Escape the inner JSON double quotes by doubling them to ""
        const escapedPermissions = (row.productPermissions || "").replace(/"/g, '""');

        // 2. Return the row with escapedPermissions inside standard CSV wrapping quotes
        return `"${row.parentUserId || ""}","${row.emailAddress || ""}","${row.userName || ""}","${row.contactNumber || ""}","${row.fullName || ""}","${row.password || ""}","${row.companyName || ""}","${row.companyLocation || ""}","${row.city || ""}","${row.address || ""}","${row.zipcode || ""}","${row.countryCode || ""}","${row.products || ""}","${escapedPermissions}","${row.aliasEmailAddress || ""}"`;
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'bulk_user_upload_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleFileChange = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];

      if (file) {
        const newFile = Object.assign(file, {
          preview: URL.createObjectURL(file),
        });
        setBulkTemplateFile(newFile);
      }
    },
    [setBulkTemplateFile]
  );

  const onSubmit = async (data) => {
    if (!bulkTemplateFile) {
      renderErrorMessage("Please upload a CSV file.");
      return;
    }
    if (bulkTemplateFile.size === 0) {
      renderErrorMessage("File is empty. Please select a valid file.");
      return;
    }
    const formData = new FormData();
    formData.append('file', bulkTemplateFile);
    setLoadingSubmit(true);

    try {
      let response = await axiosPostCall('uploadBulkUserByCsv', formData, true);
      response=response.data
      setBulkTemplateFile(null);
      setUploadErrors(null);
      if (response.errors && response.errorCount > 0) {
        setUploadErrors(response.errors);
        renderErrorMessage(response.message || "Error in bulk user upload");
      } else {
        setBulkModalOpen(false);
        reset();
        renderSuccessMessage(response.message || 'Bulk user uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading bulk templates:', error);
      if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        setUploadErrors(error.response.data.errors || error.errors);
        renderErrorMessage(error.message);
      } else {
        setUploadErrors(null);
        renderErrorMessage(error.message || 'Failed to upload bulk templates.');
      }
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2" color="textSecondary" sx={{ display: 'inline' }}>
            Upload a CSV file containing your users as per the sample CSV below
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
            <DownloadLink
              variant="body2"
              onClick={handleDownload}
            >
              Download Sample CSV
            </DownloadLink>
          </Box>
        </Box>
        <Box
          sx={{
            width: '100%',
            height: '180px',
          }}
        >
          <UploadArea
            name="file"
            onDrop={handleFileChange}
            acceptedMimes={{
              'text/csv': ['.csv'],
            }}
            sx={{ width: '100%', height: '100%' }}
          />
        </Box>
        {uploadErrors && (
          <DownloadLink
            variant="body2"
            sx={{ color: "red", "&:hover": { color: "crimson" } }}
            onClick={handleDownloadErrors}
          >
            Download Error Report
          </DownloadLink>
        )}
        <SubmitButton
          sx={{
            pointerEvents: !loadingSubmit ? 'auto' : 'none',
            opacity: loadingSubmit ? 0.75 : 1,
          }}
          onClick={handleSubmit(onSubmit)}
        >
          {!loadingSubmit && (
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '0.9rem',
                color: '#fff',
                letterSpacing: '0.01em',
              }}
            >
              Submit for bulk import
            </Typography>
          )}
          {loadingSubmit && (
            <CircularProgress
              color="inherit"
              size={18}
              sx={{ color: '#fff' }}
            />
          )}
        </SubmitButton>
      </Box>
    </FormProvider>
  );
};
export default BulkUserImportForm;
